/**
 * Data Room copy: the folders, documents, revisions, deletion and the
 * dialogs that move, rename and restore them.
 */

export interface DataRoomCopy {
  readonly dataRoom: {
    readonly title: string;
    readonly intro: string;
    readonly newDocument: string;
    readonly download: string;
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly noMatches: string;
    readonly recent: string;
    readonly empty: string;
    readonly emptyBody: string;
    readonly categories: Readonly<Record<string, string>>;
    readonly categoryDefinitions: Readonly<Record<string, string>>;
    readonly columns: {
      readonly title: string;
      readonly version: string;
      readonly updatedBy: string;
      readonly updated: string;
    };
    readonly versionLabel: (version: number) => string;
    readonly currentVersion: (version: number) => string;
    readonly updatedByLabel: string;
    readonly categoryLabel: string;
    readonly edit: string;
    readonly save: string;
    readonly saving: string;
    readonly history: string;
    readonly historyTitle: string;
    readonly currentBadge: string;
    readonly changeNoteLabel: string;
    readonly changeNoteHint: string;
    readonly view: string;
    readonly restore: string;
    readonly restoreTitle: string;
    readonly restoreBody: (version: number) => string;
    readonly restoreEffects: readonly string[];
    readonly viewingOld: (version: number, current: number) => string;
    readonly backToCurrent: string;
    readonly restoredFrom: (version: number) => string;
    readonly createTitle: string;
    readonly titleLabel: string;
    readonly titleHint: string;
    readonly contentLabel: string;
    readonly contentHint: string;
    readonly create: string;
    readonly creating: string;
    readonly errorSummary: string;
    readonly titleMissing: string;
    readonly contentMissing: string;
    readonly conflictBody: string;
    readonly toastCreated: string;
    readonly toastSaved: (version: number) => string;
    readonly toastRestored: (version: number) => string;
    readonly toastUnchanged: string;
    readonly breadcrumbLabel: string;
    readonly documentCount: (count: number) => string;
    readonly subfoldersHeading: string;
    readonly documentsHeading: string;
    readonly emptyLocation: string;
    readonly newFolder: string;
    readonly folderNameLabel: string;
    readonly newFolderBody: string;
    readonly newFolderEffects: readonly string[];
    readonly folderNameMissing: string;
    readonly renameFolder: string;
    readonly renameFolderBody: string;
    readonly renameFolderEffects: readonly string[];
    readonly moveFolder: string;
    readonly moveBody: string;
    readonly moveEffects: readonly string[];
    readonly destinationLabel: string;
    readonly deleteFolder: string;
    readonly deleteFolderTitle: string;
    readonly deleteFolderFailedTitle: string;
    readonly deleteFolderBody: (name: string) => string;
    readonly deleteFolderEffects: readonly string[];
    readonly moveDocument: string;
    readonly toastFolderCreated: string;
    readonly toastFolderRenamed: string;
    readonly toastFolderMoved: string;
    readonly toastFolderDeleted: string;
    readonly toastDocumentMoved: string;
    readonly deleteDocument: string;
    readonly deleteDocumentTitle: string;
    readonly deleteDocumentBody: (title: string) => string;
    readonly deleteDocumentEffects: readonly string[];
    readonly toastDocumentDeleted: string;
    readonly deletedDocuments: string;
    readonly deletedEmpty: string;
    readonly deletedByLabel: string;
    readonly deletedBannerTitle: string;
    readonly deletedBannerBody: (name: string) => string;
    readonly recoverDocument: string;
    readonly recoverTitle: string;
    readonly recoverBody: (title: string) => string;
    readonly recoverEffects: readonly string[];
    readonly toastDocumentRecovered: string;
  };
}

export const DATA_ROOM_COPY_EN: DataRoomCopy = {
  dataRoom: {
    title: 'Data Room',
    intro:
      'The current truth about Mouja, in versioned documents: what we are building, where we are heading, and what we have decided.',
    newDocument: 'New document',
    download: 'Download Data Room',
    searchLabel: 'Find a document',
    searchPlaceholder: 'Filter by title…',
    noMatches: 'No document title matches.',
    recent: 'Recently updated',
    empty: 'The Data Room is empty',
    emptyBody:
      'Create the first document — the PRD, the vision, or the output of a research task.',
    categories: {
      PRODUCT: 'Product',
      DIRECTION: 'Direction',
      DESIGN: 'Design',
      TECHNOLOGY: 'Technology',
      TASK_OUTPUTS: 'Task outputs',
      FUTURE: 'Future',
    },
    categoryDefinitions: {
      PRODUCT: 'What we are building: PRD, flows, stories.',
      DIRECTION: 'Where we are heading: vision and goals.',
      DESIGN: 'The design identity the product uses.',
      TECHNOLOGY: 'Tools and providers we have decided on — decisions, not research.',
      TASK_OUTPUTS: 'Research, analyses, and decisions produced by tasks.',
      FUTURE: 'Deliberately postponed: expected later, not on the current path.',
    },
    columns: {
      title: 'Document',
      version: 'Version',
      updatedBy: 'Last edited by',
      updated: 'Updated',
    },
    versionLabel: (version) => `v${String(version)}`,
    currentVersion: (version) => `Current version v${String(version)}`,
    updatedByLabel: 'Last edited by',
    categoryLabel: 'Category',
    edit: 'Edit document',
    save: 'Save as new version',
    saving: 'Saving…',
    history: 'Version history',
    historyTitle: 'Version history',
    currentBadge: 'Current',
    changeNoteLabel: 'Change note',
    changeNoteHint: 'Optional — one line on what changed and why.',
    view: 'View',
    restore: 'Restore',
    restoreTitle: 'Restore this version',
    restoreBody: (version) =>
      `Version ${String(version)}'s content becomes a new version at the top of the history.`,
    restoreEffects: [
      'A new version is created carrying the old content.',
      'Nothing in the history is rewritten or lost.',
      'The restore is recorded with your name and the source version.',
    ],
    viewingOld: (version, current) =>
      `You are reading version ${String(version)}. The current version is ${String(current)}.`,
    backToCurrent: 'Back to the current version',
    restoredFrom: (version) => `restored from v${String(version)}`,
    createTitle: 'New Data Room document',
    titleLabel: 'Title',
    titleHint: 'The document’s name in lists and in the export.',
    contentLabel: 'Content',
    contentHint: 'Markdown: headings, lists, checklists, tables, links, code, quotes.',
    create: 'Create document',
    creating: 'Creating…',
    errorSummary: 'The document needs attention before it can be created.',
    titleMissing: 'A title is required.',
    contentMissing: 'Content is required.',
    conflictBody:
      'Someone saved a newer version while you were editing. Open the current version, bring your changes over, and save again — nothing you typed has been stored.',
    toastCreated: 'Document created.',
    toastSaved: (version) => `Saved as version ${String(version)}.`,
    toastRestored: (version) => `Restored into version ${String(version)}.`,
    toastUnchanged: 'No changes to save.',
    breadcrumbLabel: 'You are here',
    documentCount: (count) =>
      count === 1 ? '1 document' : `${String(count)} documents`,
    subfoldersHeading: 'Folders',
    documentsHeading: 'Documents',
    emptyLocation: 'Nothing here yet. Create a folder or a document.',
    newFolder: 'New folder',
    folderNameLabel: 'Folder name',
    newFolderBody: 'The folder is created here, inside the place you are viewing.',
    newFolderEffects: [
      'Folders organise documents; they carry no content of their own.',
      'Up to four folder levels fit under the Data Room root.',
    ],
    folderNameMissing: 'A folder name is required.',
    renameFolder: 'Rename',
    renameFolderBody: 'Only the name changes. Everything inside stays put.',
    renameFolderEffects: [
      'Links keep working — folders are found by identity, not by name.',
      'The rename is recorded with your name.',
    ],
    moveFolder: 'Move',
    moveBody: 'Pick the destination. Everything inside moves along.',
    moveEffects: [
      'The contents move with it, history intact.',
      'The move is recorded with your name.',
    ],
    destinationLabel: 'Destination',
    deleteFolder: 'Delete',
    deleteFolderTitle: 'Delete this folder',
    deleteFolderFailedTitle: 'This folder could not be deleted',
    deleteFolderBody: (name) => `"${name}" and everything inside it will be deleted.`,
    deleteFolderEffects: [
      'Documents move to the archive with every version and audit record preserved.',
      'Nested folders are removed and their structure cannot be recovered automatically.',
      'The deletion is recorded with your name.',
    ],
    moveDocument: 'Move',
    toastFolderCreated: 'Folder created.',
    toastFolderRenamed: 'Folder renamed.',
    toastFolderMoved: 'Folder moved.',
    toastFolderDeleted: 'Folder deleted.',
    toastDocumentMoved: 'Document moved.',
    deleteDocument: 'Delete document',
    deleteDocumentTitle: 'Delete this document',
    deleteDocumentBody: (title) =>
      `"${title}" will leave the Data Room — its folders, search, submissions, and exports.`,
    deleteDocumentEffects: [
      'Every version, task reference, and audit record is preserved.',
      'Tasks that submitted it keep their exact submitted version on record.',
      'You can restore it later from Deleted documents.',
      'The deletion is recorded with your name and the time.',
    ],
    toastDocumentDeleted: 'Document deleted.',
    deletedDocuments: 'Deleted documents',
    deletedEmpty: 'No deleted documents. Everything is where it should be.',
    deletedByLabel: 'Deleted by',
    deletedBannerTitle: 'This document is deleted',
    deletedBannerBody: (name) =>
      `Deleted by ${name}. It is hidden from the Data Room until restored; its history is intact and read-only.`,
    recoverDocument: 'Restore document',
    recoverTitle: 'Restore this document',
    recoverBody: (title) => `"${title}" returns to the Data Room.`,
    recoverEffects: [
      'It returns to its previous folder — or to its category, if that folder is gone.',
      'It reappears in folders, search, submissions, and exports.',
      'The restore is recorded with your name and the time.',
    ],
    toastDocumentRecovered: 'Document restored.',
  },
};

export const DATA_ROOM_COPY_AR: DataRoomCopy = {
  dataRoom: {
    title: 'غرفة البيانات',
    intro:
      'حقيقة «موجة» الحالية في مستندات ذات إصدارات: ماذا نبني، وإلى أين نتجه، وما الذي قررناه.',
    newDocument: 'مستند جديد',
    download: 'تنزيل غرفة البيانات',
    searchLabel: 'ابحث عن مستند',
    searchPlaceholder: 'صفِّ حسب العنوان…',
    noMatches: 'لا عنوان مستند يطابق البحث.',
    recent: 'حُدِّثت مؤخرًا',
    empty: 'غرفة البيانات فارغة',
    emptyBody: 'أنشئ المستند الأول — وثيقة المتطلبات، أو الرؤية، أو ناتج مهمة بحثية.',
    categories: {
      PRODUCT: 'المنتج',
      DIRECTION: 'الاتجاه',
      DESIGN: 'التصميم',
      TECHNOLOGY: 'التقنية',
      TASK_OUTPUTS: 'مخرجات المهام',
      FUTURE: 'المستقبل',
    },
    categoryDefinitions: {
      PRODUCT: 'ما نبنيه: وثيقة المتطلبات، والمسارات، والقصص.',
      DIRECTION: 'إلى أين نتجه: الرؤية والأهداف.',
      DESIGN: 'هوية التصميم التي يعتمدها المنتج.',
      TECHNOLOGY: 'الأدوات والمزوّدون الذين قررناهم — قرارات لا أبحاث.',
      TASK_OUTPUTS: 'أبحاث وتحليلات وقرارات أنتجتها المهام.',
      FUTURE: 'مؤجَّل عن قصد: متوقع لاحقًا، وليس على المسار الحالي.',
    },
    columns: {
      title: 'المستند',
      version: 'الإصدار',
      updatedBy: 'آخر من حرّره',
      updated: 'آخر تحديث',
    },
    versionLabel: (version) => `الإصدار ${String(version)}`,
    currentVersion: (version) => `الإصدار الحالي ${String(version)}`,
    updatedByLabel: 'آخر من حرّره',
    categoryLabel: 'الفئة',
    edit: 'تحرير المستند',
    save: 'حفظ كإصدار جديد',
    saving: 'جارٍ الحفظ…',
    history: 'سجل الإصدارات',
    historyTitle: 'سجل الإصدارات',
    currentBadge: 'الحالي',
    changeNoteLabel: 'ملاحظة التغيير',
    changeNoteHint: 'اختيارية — سطر واحد عمّا تغيّر ولماذا.',
    view: 'عرض',
    restore: 'استرجاع',
    restoreTitle: 'استرجاع هذا الإصدار',
    restoreBody: (version) =>
      `يصبح محتوى الإصدار ${String(version)} إصدارًا جديدًا في أعلى السجل.`,
    restoreEffects: [
      'يُنشأ إصدار جديد يحمل المحتوى القديم.',
      'لا يُعاد كتابة أي شيء في السجل ولا يُفقد.',
      'يُسجَّل الاسترجاع باسمك ورقم الإصدار المصدر.',
    ],
    viewingOld: (version, current) =>
      `أنت تقرأ الإصدار ${String(version)}. الإصدار الحالي هو ${String(current)}.`,
    backToCurrent: 'العودة إلى الإصدار الحالي',
    restoredFrom: (version) => `استُرجع من الإصدار ${String(version)}`,
    createTitle: 'مستند جديد في غرفة البيانات',
    titleLabel: 'العنوان',
    titleHint: 'اسم المستند في القوائم وفي التنزيل.',
    contentLabel: 'المحتوى',
    contentHint: 'Markdown: عناوين، قوائم، قوائم تحقق، جداول، روابط، شيفرة، اقتباسات.',
    create: 'إنشاء المستند',
    creating: 'جارٍ الإنشاء…',
    errorSummary: 'المستند يحتاج إلى مراجعة قبل إنشائه.',
    titleMissing: 'العنوان مطلوب.',
    contentMissing: 'المحتوى مطلوب.',
    conflictBody:
      'حفظ زميل إصدارًا أحدث أثناء تحريرك. افتح الإصدار الحالي، وانقل تعديلاتك إليه، ثم احفظ من جديد — لم يُخزَّن شيء مما كتبته.',
    toastCreated: 'أُنشئ المستند.',
    toastSaved: (version) => `حُفظ كإصدار ${String(version)}.`,
    toastRestored: (version) => `استُرجع في الإصدار ${String(version)}.`,
    toastUnchanged: 'لا تغييرات للحفظ.',
    breadcrumbLabel: 'موقعك الحالي',
    documentCount: (count) => (count === 1 ? 'مستند واحد' : `${String(count)} مستندات`),
    subfoldersHeading: 'المجلدات',
    documentsHeading: 'المستندات',
    emptyLocation: 'لا شيء هنا بعد. أنشئ مجلدًا أو مستندًا.',
    newFolder: 'مجلد جديد',
    folderNameLabel: 'اسم المجلد',
    newFolderBody: 'يُنشأ المجلد هنا، داخل الموقع الذي تعرضه الآن.',
    newFolderEffects: [
      'المجلدات تنظّم المستندات ولا تحمل محتوى بذاتها.',
      'تتسع غرفة البيانات لأربعة مستويات من المجلدات كحد أقصى.',
    ],
    folderNameMissing: 'اسم المجلد مطلوب.',
    renameFolder: 'إعادة تسمية',
    renameFolderBody: 'يتغير الاسم فقط. كل ما بداخله يبقى مكانه.',
    renameFolderEffects: [
      'تبقى الروابط صالحة — تُعرف المجلدات بهويتها لا باسمها.',
      'تُسجَّل إعادة التسمية باسمك.',
    ],
    moveFolder: 'نقل',
    moveBody: 'اختر الوجهة. كل ما بداخله ينتقل معه.',
    moveEffects: ['تنتقل المحتويات معه وسجلها سليم.', 'يُسجَّل النقل باسمك.'],
    destinationLabel: 'الوجهة',
    deleteFolder: 'حذف',
    deleteFolderTitle: 'حذف هذا المجلد',
    deleteFolderFailedTitle: 'تعذّر حذف هذا المجلد',
    deleteFolderBody: (name) => `سيُحذف «${name}» وكل ما بداخله.`,
    deleteFolderEffects: [
      'تنتقل المستندات إلى الأرشيف مع بقاء جميع الإصدارات وسجل التدقيق.',
      'تُزال المجلدات الفرعية ولا يمكن استعادة بنيتها تلقائيًا.',
      'يُسجَّل الحذف باسمك.',
    ],
    moveDocument: 'نقل',
    toastFolderCreated: 'أُنشئ المجلد.',
    toastFolderRenamed: 'أُعيدت تسمية المجلد.',
    toastFolderMoved: 'نُقل المجلد.',
    toastFolderDeleted: 'حُذف المجلد.',
    toastDocumentMoved: 'نُقل المستند.',
    deleteDocument: 'حذف المستند',
    deleteDocumentTitle: 'حذف هذا المستند',
    deleteDocumentBody: (title) =>
      `سيُزال «${title}» من غرفة البيانات — من مجلداتها وبحثها وتقديماتها وتنزيلها.`,
    deleteDocumentEffects: [
      'يبقى كل إصدار ومرجع مهمة وسجل تدقيق محفوظًا.',
      'المهام التي قدّمته تحتفظ بإصدارها المُقدَّم كما هو في السجل.',
      'يمكنك استعادته لاحقًا من «المستندات المحذوفة».',
      'يُسجَّل الحذف باسمك ووقته.',
    ],
    toastDocumentDeleted: 'حُذف المستند.',
    deletedDocuments: 'المستندات المحذوفة',
    deletedEmpty: 'لا مستندات محذوفة. كل شيء في مكانه.',
    deletedByLabel: 'حذفه',
    deletedBannerTitle: 'هذا المستند محذوف',
    deletedBannerBody: (name) =>
      `حذفه ${name}. يبقى مخفيًّا عن غرفة البيانات حتى استعادته؛ وسجله سليم وللقراءة فقط.`,
    recoverDocument: 'استعادة المستند',
    recoverTitle: 'استعادة هذا المستند',
    recoverBody: (title) => `يعود «${title}» إلى غرفة البيانات.`,
    recoverEffects: [
      'يعود إلى مجلده السابق — أو إلى فئته إن كان المجلد قد أُزيل.',
      'يظهر من جديد في المجلدات والبحث والتقديمات والتنزيل.',
      'تُسجَّل الاستعادة باسمك ووقتها.',
    ],
    toastDocumentRecovered: 'استُعيد المستند.',
  },
};
